import React, { useState, forwardRef } from "react";
import { InputProps } from "./Input.props";
import P from "../P/P";
import SvgIcon from "../SvgIcon/SvgIcon";
import {
	EyeCloseIcon,
	EyeOpenIcon,
	// PaperClip,
} from "@assets/icons";

const Input = forwardRef<HTMLInputElement, InputProps>(
	(
		{
			type = "text",
			label,
			error,
			onChange,
			containerClassName = "",
			inputClassName = "",
			labelClassName = "",
			text = "black",
			leftIcon,
			rightIcon,
			disabled = false,
			// NEW props for file upload (icon + onFileChange)
			onFileChange, // callback for file input
			...props
		},
		ref
	) => {
		const [showPassword, setShowPassword] = useState(false);

		const togglePasswordVisibility = () => {
			if (!disabled) setShowPassword((prev) => !prev);
		};

		// Decide actual inputType (toggle pass => text)
		const inputType =
			type === "password"
				? showPassword
					? "text"
					: "password"
				: type === "phone"
				? "tel"
				: type;

		return (
			<div className={`flex flex-col gap-1 ${containerClassName}`}>
				{/* LABEL (if provided) */}
				{label && (
					<P
						size="t1"
						type="medium"
						className={`text-${text} ${labelClassName}`}
					>
						{label}
					</P>
				)}

				<div
					className={`relative grid ${
						type === "phone"
							? "grid-cols-maxFr border border-strokeColor rounded-lg py-2 px-4 hover:border-mainColor"
							: "grid-cols-1"
					} w-full items-center ${
						disabled && type === "phone"
							? "bg-grayThree cursor-not-allowed"
							: ""
					}`}
				>
					{type === "phone" && (
						<div className="grid grid-cols-1 place-content-center place-items-center border-r border-strokeColor pr-2">
							<P size="p2" type="medium">
								+993
							</P>
						</div>
					)}

					<div className={`relative grid grid-cols-1 w-full items-center`}>
						{/* LEFT ICON if provided */}
						{leftIcon && <span className="absolute left-3">{leftIcon}</span>}

						{/* MAIN INPUT */}
						<input
							ref={ref}
							type={inputType}
							onChange={onChange}
							className={`input ${onFileChange ? "pr-10" : ""} ${
								type === "phone"
									? "border-none"
									: "border border-strokeColor rounded-lg minPhone:py-2 lg:py-2 px-4 hover:border-mainColor focus:border-mainColor"
							} ${
								leftIcon ? "pl-10" : "pl-4"
							} focus:outline-none transition-all placeholder:text-grayThree duration-300 ease-in-out focus:ring-0 focus:ring-transparent font-regular text-p3 ${
								disabled ? "bg-grayThree cursor-not-allowed" : ""
							} ${inputClassName} ${
								error ? "border-red-500 focus:ring-red-500" : ""
							}`}
							disabled={disabled}
							{...props}
						/>

						{rightIcon && <span className="absolute right-3">{rightIcon}</span>}

						{/* PASSWORD toggler (Eye icon) */}
						{type === "password" && (
							<span
								className="absolute cursor-pointer right-3"
								onClick={!disabled ? togglePasswordVisibility : undefined}
							>
								<SvgIcon Icon={showPassword ? EyeOpenIcon : EyeCloseIcon} />
							</span>
						)}

						{/* 
              NEW: If you want an absolute-positioned icon for file upload,
              we check if you provided 'onFileChange'.
              This won't conflict with the phone or password logic.
            */}
						{onFileChange && (
							<label
								htmlFor="chat-file-upload"
								className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
								title="Attach an image"
							>
								{/* <SvgIcon Icon={PaperClip} className="fill-passiveColor" /> */}
								<input
									id="chat-file-upload"
									type="file"
									accept="image/*"
									className="hidden"
									onChange={onFileChange}
								/>
							</label>
						)}
					</div>
				</div>

				{/* ERROR */}
				{error && <p className="mt-1 text-sm text-red-500">{error}</p>}
			</div>
		);
	}
);

export default React.memo(Input);
